// TKNE screen help (the "?" on the TKNE project list and on every open project). Plain words;
// keep it in step with apps/mobile/src/tools/TkneTool.tsx.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const tkneHelp: ScreenHelpContent = {
  title: 'TKNE',
  intro:
    "TKNE is where you turn something big into steps you can actually do. Each project gets its own page with five parts, Orient, Resources, Directions, Execute and Revise (together: O.R.D.E.R). Your steps can also show up as tasks in KYRS.",
  sections: [
    {
      heading: 'Your project list',
      body: [
        'Your projects sit in columns, one column per group, with an UNGROUPED column for the rest. Each project is a hexagon with its name on it.',
        'Tap a hexagon to open it out and see its steps as a row of circles. Tap a circle to jump straight to that step. Double-tap the hexagon to open the project page.',
        'Long-press (or right-click) a hexagon for Rename, Add to Group or Change Group, Set Inactive or Set Active, and Delete. Delete asks you first.',
        'Tap a group name for Rename, SHAPE sector, Add project here and Delete group. Deleting a group never deletes its projects; they move to Ungrouped.',
      ],
    },
    {
      heading: 'Adding projects and groups',
      body: [
        'Press the gear at the top right, then + Add project or + Add group. You can also press + Project at the bottom of any column.',
        'For a project, type a name, pick a group if you want one, and press Add. The new hexagon appears in its column; it does not open by itself.',
        'The gear also has a Compass switch. On shows the full compass above your projects, with a Layer choice: TKNE, SOLO or Off. Off hides the compass again.',
      ],
    },
    {
      heading: 'On a project page',
      body: [
        'At the top: ‹ Back, the project name (tap it to rename), TKNE, Export and Delete Order.',
        'Under that is a row of circles showing your steps, and a Status box. Use Status to write where things stand right now. Drag the little grip at its bottom edge to make it taller.',
        'The TKNE button opens an assistant panel with four ways to work: Manual (it only answers when you ask), Guided (it asks you questions one at a time), Template (you say what kind of project it is and it proposes stages for you to approve), and Direct (you describe the project and it fills in the whole form). There are also Chats and Files for past chats and attached files.',
        'Export lets you pick PDF or Word (.doc).',
      ],
    },
    {
      heading: 'The five parts (O.R.D.E.R)',
      body: [
        'Each part is a coloured bar. Tap the bar to open or close it. The small i next to a title explains that part.',
      ],
      items: [
        { term: 'Orient', text: 'where you are going. Write the outcome and why it matters. Pick a Sector (None, Social, Health, Admin, Personal or Economic) and set the Drift gap (days), a number the compass uses for this project. It starts at 2.' },
        { term: 'Resources', text: 'what you need to get there: people, tools, skills, info, money, time. Press + on the bar to add a line.' },
        { term: 'Directions', text: 'the steps on the path, in order. This is the main part, so it has its own section below.' },
        { term: 'Execute', text: 'concrete things to do next. Press + on the bar to add a task. There is also a box for how the work gets done. Each task has a dropdown to pick one of your Directions sub-steps.' },
        { term: 'Revise', text: 'a free notes box for what changed, what you learned, what you need to rethink.' },
      ],
    },
    {
      heading: 'Directions: steps and sub-steps',
      body: [
        'Press + Add step at the bottom. Each STEP has a coloured status box on the left, its number, a text box, a Date button and a Run as protocol button.',
        'The status box opens a small menu: P is Pending, S is Active, C is Completed. The text box changes colour to match.',
        'The + circle on a step adds a sub-step under it, and sub-steps can have their own sub-steps. The ▲ and ▼ move a step up or down. The red mark removes it.',
        'Date opens a calendar with Clear and Done. Tap the step number to write notes for that step.',
        'Run as protocol makes a protocol in THEMIS from the step and its sub-steps and opens it. Once made, the button says Open protocol.',
      ],
    },
    {
      heading: 'The ⋯ menu: more tools',
      body: [
        'When a part is open, its ⋯ button shows switches. Basic is on; turn the others on to add them to that part.',
      ],
      items: [
        { term: 'Orient', text: 'General Information (dates, problem, user, objectives, core feature, exclusions, Definition of Done, MVP), Constraints (challenges, constraints, risks) and Forecast (a START to FINISH timeline).' },
        { term: 'Resources', text: 'Essential Table, a grid with People, Places, Processes, Products and Predictions. You can add rows and columns.' },
        { term: 'Directions', text: 'Calendar (pick a KRNOS calendar to see and add its events), Storm (an empty grid for brainstorming), List (a simple tick list) and Task.' },
        { term: 'Task', text: 'pick a KYRS list, or press Add list to make one. Your Directions steps then copy into that list as tasks. You can also add tasks here. Ticking a task in KYRS marks the step Completed.' },
        { term: 'Execute', text: 'Metis-Order, a log of working sessions. Each session has a date, what you will finish, the deliverable, what you need, which step you are on, timed tasks, and what happened (None, Success, Unsuccessful or N/A). Use the arrows to move between sessions and + for a new one.' },
        { term: 'Revise', text: 'Notes (Process Negative, Index, Upgrades, Additional Notes) and Logs. In Logs, write a note and press + Log; notes are grouped by day.' },
      ],
    },
    {
      heading: 'What saves',
      body: [
        'There is no Save button. Whatever you type or tick on a project page saves by itself a moment after you stop. Group changes, Task list items and Logs save the moment you make them.',
        'Delete Order removes the whole project and asks you first.',
      ],
    },
  ],
};
